import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/components/Box";
import DonutScore from "@memorang/ui/components/DonutScore";
import TooltipWrapper from "@memorang/ui/components/TooltipWrapper";
import { useEffect, useState } from "react";
import type { GestureResponderEvent } from "react-native";
import { IconButton, List, Text } from "react-native-paper";
import type { MixedItemTag } from "types/tag";
import AnswerStatusRow from "./AnswerStatusRow";

type TopicListItemProps = {
	title: string;
	numItems: number;
	numCorrect?: number;
	numIncorrect?: number;
	numSkipped?: number;
	children?: MixedItemTag[];
	isChildTag?: boolean;
	isExpanded?: boolean;
	onExpandChange?: (expanded: boolean) => void;
};

const TopicListItem = ({
	title,
	numItems,
	numCorrect = 0,
	numIncorrect = 0,
	numSkipped = 0,
	children,
	isChildTag,
	isExpanded,
	onExpandChange,
}: TopicListItemProps) => {
	const accuracy = Math.round((numCorrect / numItems) * 100);
	const hasChildTags = children?.length! > 0;
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const scoreRanges = useAppStore((state) => state.app?.scoreRanges);
	const [expanded, setExpanded] = useState(false);
	const [childrenExpanded, setChildrenExpanded] = useState(false);

	useEffect(() => {
		if (isExpanded !== undefined) {
			setExpanded(isExpanded);
			setChildrenExpanded(isExpanded);
		}
	}, [isExpanded]);

	const handlePress = () => {
		const newExpandedState = !expanded;
		setExpanded(newExpandedState);
		if (onExpandChange) {
			onExpandChange(newExpandedState);
		}
	};

	const toggleExpandAll = (e: GestureResponderEvent) => {
		e.stopPropagation();
		const newExpandedState = !childrenExpanded;
		setChildrenExpanded(newExpandedState);
		setExpanded(newExpandedState);
		if (onExpandChange) {
			onExpandChange(newExpandedState);
		}
	};

	return (
		<List.Accordion
			title={title}
			style={{
				backgroundColor: isMobile ? undefined : theme.colors.elevation.level1,
				paddingLeft: isChildTag ? 16 : 0,
			}}
			expanded={expanded}
			titleNumberOfLines={4}
			rippleColor={hasChildTags ? undefined : "transparent"}
			onPress={hasChildTags ? handlePress : undefined}
			left={(props) => {
				return (
					<List.Icon
						{...props}
						icon={() => {
							return (
								<DonutScore
									scoreRanges={scoreRanges}
									distribution={[numCorrect, numItems - numCorrect]}
									score={accuracy}
									variant="progress"
									customChildren={
										<Text
											variant="labelSmall"
											style={{
												fontSize: 10,
												fontWeight: "600",
											}}
										>
											{accuracy}%
										</Text>
									}
									radius={28}
									innerRadius={22}
								/>
							);
						}}
					/>
				);
			}}
			description={
				isMobile ? (
					<AnswerStatusRow
						numCorrect={numCorrect}
						numIncorrect={numIncorrect}
						numSkipped={numSkipped}
					/>
				) : null
			}
			descriptionStyle={{
				paddingTop: 8,
			}}
			right={({ isExpanded }) => {
				return (
					<List.Icon
						// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
						icon={() => {
							return (
								<Box flexDirection="row" alignItems="center" gap={8}>
									{isMobile ? null : (
										<AnswerStatusRow
											numCorrect={numCorrect}
											numIncorrect={numIncorrect}
											numSkipped={numSkipped}
											tagLabel={title}
										/>
									)}
									{hasChildTags ? (
										<>
											{!isChildTag && (
												<TooltipWrapper
													title={
														childrenExpanded ? "Collapse all" : "Expand all"
													}
												>
													<IconButton
														icon={
															childrenExpanded
																? "collapse-all-outline"
																: "expand-all-outline"
														}
														size={20}
														onPress={toggleExpandAll}
														style={{
															margin: 0,
														}}
													/>
												</TooltipWrapper>
											)}
											<Icon
												name={isExpanded ? "chevron-up" : "chevron-down"}
												size={24}
											/>
										</>
									) : null}
								</Box>
							);
						}}
					/>
				);
			}}
		>
			{hasChildTags
				? children?.map((childTag) => (
						<TopicListItem
							key={childTag.id}
							{...childTag}
							title={
								childTag.code
									? `${childTag.code} - ${childTag.label || ""}`
									: childTag.label || ""
							}
							isChildTag={true}
							numItems={childTag.numItems}
							isExpanded={childrenExpanded}
							onExpandChange={(expanded) => {
								if (!expanded && childrenExpanded) {
									setChildrenExpanded(false);
								}
							}}
						/>
					))
				: null}
		</List.Accordion>
	);
};

export default TopicListItem;
